import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL
});

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Books API
export const booksAPI = {
  getAll: () => api.get('/books'),
  getById: (id) => api.get(`/books/${id}`),
  create: (data) => api.post('/books', data),
  update: (id, data) => api.put(`/books/${id}`, data),
  delete: (id) => api.delete(`/books/${id}`),
  borrow: (id, customerId, returnDate) => api.post(`/books/${id}/borrow`, { customer_id: customerId, return_date: returnDate }),
  return: (id, data) => api.post(`/books/${id}/return`, data || {}),
  addNote: (id, note) => api.post(`/books/${id}/notes`, { note }),
  updateNote: (noteId, note) => api.put(`/books/notes/${noteId}`, { note }),
  deleteNote: (noteId) => api.delete(`/books/notes/${noteId}`),
  checkDuplicate: (data) => api.post('/books/check/duplicate', data),
  addBookCopy: (data) => api.post('/books/copy/add', data),
  getCategoriesByDepartment: (departmentId) => api.get(`/books/filter/categories/${departmentId}`),
  getDepartmentsByCategory: (categoryId) => api.get(`/books/filter/departments/${categoryId}`),
  export: (filters) => api.get('/export/books', { params: filters, responseType: 'blob' }),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/books', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  processImportDuplicates: (books) => api.post('/import/books/process-duplicates', { books })
};

// Customers API
export const customersAPI = {
  getAll: () => api.get('/customers'),
  getById: (identity_id) => api.get(`/customers/${identity_id}`),
  create: (data) => api.post('/customers', data),
  update: (identity_id, data) => api.put(`/customers/${identity_id}`, data),
  updateSubscription: (identity_id, data) => api.put(`/customers/${identity_id}/subscription`, data),
  addNote: (identity_id, note) => api.post(`/customers/${identity_id}/notes`, { note }),
  addPayment: (identity_id, data) => api.post(`/customers/${identity_id}/payment`, data),
  getPaymentHistory: (identity_id) => api.get(`/customers/${identity_id}/payment-history`),
  getActivityHistory: (identity_id) => api.get(`/customers/${identity_id}/activity-history`),
  addActivityLog: (identity_id, data) => api.post(`/customers/${identity_id}/activity-history`, data),
  borrowBook: (identity_id, data) => api.post(`/customers/${identity_id}/borrow`, data),
  returnBook: (identity_id, data) => api.post(`/customers/${identity_id}/return`, data),
  extendLoan: (identity_id, loan_id, data) => api.post(`/customers/${identity_id}/loans/${loan_id}/extend`, data),
  delete: (identity_id) => api.delete(`/customers/${identity_id}`),
  export: () => api.get('/export/customers', { responseType: 'blob' }),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/customers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importFromExcel: (formData) => api.post('/import/customers/excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  processDuplicateSubscriptions: (data) => api.post('/customers/import/process-duplicates', data)
};

// Reports API
export const reportsAPI = {
  getReports: (filters) => api.get('/reports', { params: filters }),
  getStatistics: () => api.get('/reports/statistics'),
  export: (filters) => api.get('/export/reports', { params: filters, responseType: 'blob' })
};

// Departments API
export const departmentsAPI = {
  getAll: () => api.get('/departments'),
  add: (data) => api.post('/departments', data)
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  add: (data) => api.post('/categories', data)
};

// Auth API
export const authAPI = {
  register: (username, email, password, role = 'user') => 
    api.post('/auth/register', { username, email, password, role }),
  login: (username, password) => 
    api.post('/auth/login', { username, password }),
  getCurrentUser: (token) => 
    api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  updateOne: (key, value) => api.post(`/settings/${key}`, { setting_value: value }),
  updateMultiple: (settings) => api.post('/settings', settings)
};

// Set token in headers
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

// Get stored token
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

export default api;
